import { getBooleanEnvValue } from './env.utils'

/**
 * Kill-switch for the SVD (Smart Variant Discovery) program's **web/browser traffic**. BOTH web
 * surfaces gate on it, on top of the shared `ramp_svd-rollout-web` experiment:
 *  - the product-card flow (server `getServerSideBaseProps` → `svd.config` re-export → `shouldUseSvdCard`)
 *  - the PDP buy-box flow (`useIsSvdEnabled`)
 *
 * NOT the app switch: native-app web-views bypass this flag entirely and ramp/kill on the
 * `SVD_APP_WEBVIEW_GATE` Statsig gate alone (see below, and `resolveIsSvdEnabled` /
 * `getSvdExperimentValue` — both fork on webview BEFORE reading this flag). That means killing SVD
 * program-wide is TWO switches: this flag (web, deploy-time) plus the gate (app, runtime).
 *
 * Env-driven via `NEXT_PUBLIC_ENABLE_SVD_FLOW_DISCOVERY` — **true in every env except prod and prod-eu**
 * (prod-safe default `false`). While off, both web surfaces stay dark regardless of the experiment bucket
 * (card: server skips the eval; PDP: `useIsSvdEnabled` returns false before the Statsig call). Per-env
 * values live in `.helm/values-*.yaml` + `env/.env.deploy-*` (+ `.env` / `.env.local` for local dev).
 *
 * SCOPE NOTE — this drives the SVD *rendering* surfaces plus `fetchSlugInfo`'s `site-paths` version
 * (`SITE_PATHS_API_VERSION`). It deliberately does NOT drive the sitemap routes: those are gated
 * separately by `ENABLE_SVD_SITEMAPS` below, because a crawler-facing document has a different risk
 * profile than a rendered page and must be ramped on its own schedule (ADR 0002).
 *
 * Reads only `env.utils` ON PURPOSE, so it stays dependency-free / client-safe: it's imported by both
 * client (PDP hook, card render path) and server code and must never drag in the statsig registry.
 */
export const ENABLE_SVD_FLOW_DISCOVERY = getBooleanEnvValue(process.env.NEXT_PUBLIC_ENABLE_SVD_FLOW_DISCOVERY, false, {
  required: false,
})

/**
 * Kill-switch for the SVD half of the **sitemap** routes only — i.e. which BFF `site-paths` version
 * the two crawler-facing feeds read (`SITEMAP_SITE_PATHS_API_VERSION` → `fetchSiteMapV2` and
 * `fetchPDPColorsSiteMap`). Separate from `ENABLE_SVD_FLOW_DISCOVERY` ON PURPOSE.
 *
 * Why its own flag (STRFRNT-15810 · ADR 0002): the rendering surfaces and the sitemaps fail
 * differently. A wrong card renders one bad page for one user and is a hard refresh away from
 * correct; a wrong sitemap hands Google a durable document. SAND `/products/v2/slugs` currently
 * under-returns (329 fewer slugs, ~5.8k fewer variant URLs than v1 — see ADR 0002's parity table),
 * so the sitemaps must be able to stay on v1 while the card/PDP/slug-info flow moves to v2, and to
 * move to v2 per-env once the parity gate passes. One flag for both would force those two decisions
 * to be the same decision.
 *
 * Env-driven via `NEXT_PUBLIC_ENABLE_SVD_SITEMAPS` — **true in every env except prod and prod-eu**
 * (prod-safe default `false`). Per-env values live in `.helm/values-*.yaml` + `env/.env.deploy-*`.
 * `NEXT_PUBLIC_*` is build-inlined, so flipping it is a deploy, not a runtime toggle.
 */
export const ENABLE_SVD_SITEMAPS = getBooleanEnvValue(process.env.NEXT_PUBLIC_ENABLE_SVD_SITEMAPS, false, {
  required: false,
})

/**
 * Statsig feature gate ramping SVD for native-app web-views (the post-order screens the app renders
 * in a webview — order details / cancel / returns). App traffic must NEVER read the web experiment
 * (`ramp_svd-rollout-web`) and web traffic must never read this gate, so neither surface pollutes
 * the other's exposure analytics and the two ramp independently. This gate is the app surface's
 * ONLY switch — webview reads deliberately bypass the `ENABLE_SVD_FLOW_DISCOVERY` kill-switch above
 * (both server `resolveIsSvdEnabled` and client `getSvdExperimentValue`), so the app can ramp on
 * prod while the web flag is still `false` and be killed at runtime from the Statsig console
 * instead of a redeploy. Lives here (not the statsig registry) for the same reason as the
 * kill-switch: shared by server (`get-server-side-props`) and client (`get-svd-experiment-value`)
 * without dragging in the registry.
 *
 * The gate's console rules can target `custom.app_version` / `app_ota_version` (version comparator)
 * — every webview request carries them, parsed from the UA token, so per-build rollout needs no
 * further FE work.
 *
 * RULE-BASED TARGETING ONLY — NEVER A PERCENTAGE ROLLOUT. Server-side this is evaluated against the
 * constant `STATSIG_ANONYMOUS_USER_ID` (same as every gate in `FEATURE_GATE_REGISTRY`), because the
 * request user does not exist on the app's initial web-view request. A partial percentage therefore
 * hashes identically for every request and resolves uniformly for ALL app traffic — all-on or
 * all-off, not half the users. Use app-version rules and 0%/100%. Confirmed with product
 * (2026-08-17): this gate is a strict boolean, no ramp. See `app-version-feature-gate.server.ts`.
 *
 * MERGE PREREQUISITE: the gate must exist (created + saved) in the SAME Statsig project as both the
 * server secret and the web client SDK key (`1FagQgwoVYHQOUj4R2Hion`) BEFORE this ships. Until it
 * does, server evaluation returns `Unrecognized` → every app webview session silently drops to
 * flag-off (including users currently in the web experiment's `show` group) and
 * `user-agnostic-feature-gate.server.ts` warn-logs on every webview request; the client `checkGate`
 * reads a gate the bootstrap payload never contained.
 * Console: https://console.statsig.com/1FagQgwoVYHQOUj4R2Hion/gates/is_svd_enabled_on_app
 */
export const SVD_APP_WEBVIEW_GATE = 'is_svd_enabled_on_app'

/**
 * TEMPORARY (STRFRNT-15810 · CLP/Home SVD preview) — header that asks PL for SVD-shaped cards.
 *
 * For SVD-flow users we send this, unchanged host/endpoint, on every PL call site that can render a
 * product card on CLP or Home, so the response carries SVD-shaped cards (`anchorAttributes` /
 * `selectedAttributes` / `displayConfig` / `spId`) — letting us exercise the SVD product card on real PL
 * data. Deliberately excluded from PDP (owns its own SVD integration). Wired call sites, all gated on
 * `isSvdEnabled` — a single sitewide flag, no per-page/per-surface gate (STRFRNT-15810):
 *  - CLP + Home `layouts/fetch` (SSR) — `getCollectionPageDataPL`, `fetchSimplePLLayout`
 *  - CLP client `widgets/fetch` (single-widget pagination) — `fetch-widget-data.ts`
 *  - Layout-level "load more widgets" pagination — `fetch-layout-data.ts` (excludes PDP explicitly via
 *    `svdEnrichHeaderEnabled && this.pageType !== ProtoPageType.PDP`, since PDP paginates through the
 *    same shared `LayoutDataFetchingService`)
 *  - CLP filter/sort refresh — `use-clp-filters.ts` → `fetch-layout-data.ts`
 *  - Search PL fetch (`useSearchResults.ts` — initial/filtered `layouts/fetch`, filter-refresh and
 *    load-more `widgets/fetch-data`): the same header enrichment carries the Algolia `searchObjectId`
 *    attribution field on `products[i].responseContext` (STRFRNT-16013) that the search grid's
 *    `isPlSvdCard` branch renders `SvdProductCard` off of.
 *
 * Verified live on pre-integ (2026-08-07): the header is a no-op there today (that PL build already
 * emits SVD shape unconditionally) but is sent anyway per PL's ask, ready for environments/behavior
 * where it does gate the shape.
 *
 * Delete alongside every `isPlSvdCard`/`mapPlSvdCard` call site (and every one of the wired sites above)
 * once every PL environment emits the canonical SVD shape unconditionally and neither surface needs a
 * shape-guard fallback to the legacy card.
 */
// eslint-disable-next-line i18next/no-literal-string -- HTTP header name, not user-facing copy
export const SVD_ENRICH_VERSION_HEADER = 'x-enrich-version'
// eslint-disable-next-line i18next/no-literal-string -- HTTP header value, not user-facing copy
export const SVD_ENRICH_VERSION_VALUE = 'V2'
